import { useEffect, useMemo, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import GridViewIcon from '@mui/icons-material/GridView';
import TableRowsIcon from '@mui/icons-material/TableRows';
import type { SelectChangeEvent } from '@mui/material/Select';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';

import { useTheme } from '@mui/material/styles';

import { searchListings, type SearchParams, type SearchResponse } from '../services/searchApi';
import { ListingCard } from '../components/search/ListingCard';
import { ListingTable } from '../components/search/ListingTable';
import { SaveSearchButton } from '../components/search/SaveSearchButton';
import { SponsoredListingsSection } from '../components/search/SponsoredListingsSection';
import { PromotedListingsSection } from '../components/search/PromotedListingsSection';
import { FilterSidebar } from '../components/filters/FilterSidebar';
import { ActiveFilterChips } from '../components/filters/ActiveFilterChips';
import { parseSearchParams, setPage, setSort, type SortOption } from '../utils/searchUrl';
import { useListingViewMode, type ListingViewMode } from '../utils/viewMode';
import { ApiError } from '../services/api';
import { Seo } from '../components/seo/Seo';
import { trackSearch } from '../lib/analytics/events';
import { buildCanonicalUrl } from '../utils/seo/canonical';
import { caliberLandingPathForSearch } from '../utils/caliberLanding';
import { breadcrumbList, itemList, searchBreadcrumbCrumbs } from '../utils/seo/jsonLd';

const SORT_LABELS: Array<{ value: SortOption; label: string }> = [
  { value: 'relevance', label: 'Relevance' },
  { value: 'pricePerRound', label: 'Price per round (low → high)' },
  { value: 'newest', label: 'Newest' },
  { value: 'roundCount', label: 'Round count' },
  { value: 'retailerTrust', label: 'Retailer trust' },
];

const PAGE_SIZE_DEFAULT = 24;

/**
 * Format the "last updated" notice shown above the result list. Prefer the
 * API's `mostRecentUpdate` — the freshest listing across the WHOLE matching
 * set — so a daily-updated catalog isn't reported as stale just because the
 * relevance-top page happens to hold unchanged items (#290). Falls back to the
 * max across visible hits for older API responses that omit the field.
 */
function mostRecentUpdate(response: SearchResponse | undefined): string | null {
  if (!response) return null;
  if (response.mostRecentUpdate) {
    const t = Date.parse(response.mostRecentUpdate);
    if (Number.isFinite(t)) return new Date(t).toLocaleString();
  }
  let latest = 0;
  for (const hit of response.results) {
    if (!hit.lastUpdated) continue;
    const t = Date.parse(hit.lastUpdated);
    if (Number.isFinite(t) && t > latest) latest = t;
  }
  return latest > 0 ? new Date(latest).toLocaleString() : null;
}

/**
 * Build the search-results SEO title from the filter state. Goal: a
 * browser-tab + Google-snippet title that reads like the search a
 * shopper would have typed — "9mm Luger 124 gr JHP Federal Ammo".
 *
 * Token order matches how retailers cluster keywords in product
 * titles: caliber → grain → bullet type → brand → use case, then a
 * trailing "Ammo". The free query string (`?q=…`) leads the whole
 * stack when present so explicit searches override the filter-
 * derived order. Empty filter state falls back to "Ammo Search" so
 * the tab never goes blank during a refetch.
 *
 * The Seo wrapper appends "— Round Recon" automatically (see
 * components/seo/Seo.tsx).
 */
function buildSeoTitle(query: ReturnType<typeof parseSearchParams>): string {
  const parts: string[] = [];
  if (query.q) parts.push(query.q);
  if (query.caliber.length > 0) parts.push(query.caliber.join(' / '));
  if (query.bulletWeightGrains.length > 0) {
    parts.push(`${query.bulletWeightGrains.join('/')} gr`);
  }
  if (query.bulletType.length > 0) parts.push(query.bulletType.join(' / '));
  if (query.brand.length > 0) parts.push(query.brand.join(' / '));
  if (query.useCases.length > 0) {
    // Use cases come from the taxonomy as kebab-case slugs (e.g.
    // "defensive-handgun"); humanize for the title.
    const humanized = query.useCases.map((u) => u.replace(/-/g, ' '));
    parts.push(humanized.join(' / '));
  }
  if (parts.length === 0) return 'Ammo Search';
  return `${parts.join(' ')} Ammo`;
}

function buildSeoDescription(
  query: ReturnType<typeof parseSearchParams>,
  data: SearchResponse | undefined
): string {
  const total = data?.totalResults;
  const grainBit =
    query.bulletWeightGrains.length > 0 ? `${query.bulletWeightGrains.join('/')} gr` : '';
  const filters = [
    query.caliber.join(', '),
    grainBit,
    query.bulletType.join(', '),
    query.brand.join(', '),
  ]
    .filter(Boolean)
    .join(' · ');
  const subject = filters || query.q || 'ammunition';
  if (total != null) {
    return `${total} in-stock ${subject} listings across major retailers — sorted by price per round, freshness, and retailer trust.`;
  }
  return `Find ${subject} listings across major retailers — sorted by price per round, freshness, and retailer trust.`;
}

function buildSearchJsonLd(
  data: SearchResponse | undefined,
  canonical: string,
  calibers: string[]
): object[] {
  const out: object[] = [breadcrumbList(searchBreadcrumbCrumbs(calibers, canonical))];
  if (data && data.results.length > 0) {
    out.push(
      itemList(
        data.results.slice(0, 20).map((hit, idx) => ({
          position: idx + 1,
          name: hit.title ?? undefined,
          url: hit.visitUrl,
        }))
      )
    );
  }
  return out;
}

function paramsToApi(query: ReturnType<typeof parseSearchParams>): SearchParams {
  return {
    q: query.q || undefined,
    page: query.page,
    limit: query.limit,
    sort: query.sort,
    // Only send sortDir when the sort is not relevance (which ignores direction).
    sortDir: query.sort !== 'relevance' ? query.sortDir : undefined,
    caliber: query.caliber.length > 0 ? query.caliber : undefined,
    ammoType: query.ammoType.length > 0 ? query.ammoType : undefined,
    brand: query.brand.length > 0 ? query.brand : undefined,
    productLine: query.productLine.length > 0 ? query.productLine : undefined,
    bulletType: query.bulletType.length > 0 ? query.bulletType : undefined,
    bulletWeightGrains: query.bulletWeightGrains.length > 0 ? query.bulletWeightGrains : undefined,
    caseMaterial: query.caseMaterial.length > 0 ? query.caseMaterial : undefined,
    retailerId: query.retailerId.length > 0 ? query.retailerId : undefined,
    useCases: query.useCases.length > 0 ? query.useCases : undefined,
    minPricePerRound: query.minPricePerRound,
    maxPricePerRound: query.maxPricePerRound,
    minRoundCount: query.minRoundCount,
    stockStatus: query.stockStatus,
    inStockOnly: query.inStockOnly,
    excludeRemanufactured: query.excludeRemanufactured,
    lotNumber: query.lotNumber,
  };
}

/**
 * Search results page (spec §15.3).
 *
 * Layout:
 *   ┌──────────────┬────────────────────────────────┐
 *   │ FilterSidebar│ active chips · sort · count    │
 *   │              │ result grid                    │
 *   │              │ pagination                     │
 *   │              │ sponsored slot (announce-ready)│
 *   └──────────────┴────────────────────────────────┘
 *
 * The URL is the source of truth for filter state. Every control writes
 * back into `setSearchParams`, which triggers React Query to refetch.
 */
export function SearchResultsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = useMemo(() => parseSearchParams(searchParams), [searchParams]);
  const apiParams = useMemo(() => paramsToApi(query), [query]);
  const [viewMode, setViewMode] = useListingViewMode();
  const theme = useTheme();
  const t = theme.palette.tactical;

  const searchQuery = useQuery<SearchResponse>({
    queryKey: ['search', apiParams],
    queryFn: () => searchListings(apiParams),
    // Keep previous results visible while a refetch is in flight (avoids
    // a jarring blank state on every filter change).
    placeholderData: (prev) => prev,
  });

  // GA4 `search` event (#256) — once per distinct search with intent (free text
  // or a caliber). Consent-gated inside trackSearch; no-op without analytics
  // consent / GA configured.
  const lastTrackedSearch = useRef<string | null>(null);
  useEffect(() => {
    if (!searchQuery.data) return;
    const hasIntent = Boolean(query.q?.trim()) || query.caliber.length > 0;
    if (!hasIntent) return;
    const sig = JSON.stringify({ q: query.q, c: query.caliber, p: query.page });
    if (lastTrackedSearch.current === sig) return;
    lastTrackedSearch.current = sig;
    trackSearch({
      searchTerm: query.q || undefined,
      caliber: query.caliber[0] || undefined,
      results: searchQuery.data.totalResults,
    });
  }, [searchQuery.data, query]);

  const totalPages = searchQuery.data
    ? Math.max(
        1,
        Math.ceil(searchQuery.data.totalResults / (searchQuery.data.limit || PAGE_SIZE_DEFAULT))
      )
    : 1;

  function onSortChange(event: SelectChangeEvent<string>) {
    const next = setSort(searchParams, event.target.value as SortOption);
    setSearchParams(next);
  }

  function onSortChangeFromTable(nextSort: SortOption) {
    // setSort handles toggle (same column → flip direction) and reset
    // (new column → default direction) based on current URL state.
    setSearchParams(setSort(searchParams, nextSort));
  }

  function onPageChange(_e: React.ChangeEvent<unknown>, page: number) {
    setSearchParams(setPage(searchParams, page));
  }

  function onClearFilters() {
    const next = new URLSearchParams();
    if (query.q) next.set('q', query.q);
    setSearchParams(next);
  }

  function onViewModeChange(_e: React.MouseEvent<HTMLElement>, next: ListingViewMode | null) {
    if (next) setViewMode(next);
  }

  // There's something worth saving when the user has typed a query or
  // applied at least one filter (anything in the URL beyond paging/sort/
  // view defaults). Used to disable the "Save search" button on a bare
  // /search with no criteria.
  const hasCriteria = useMemo(() => {
    if (query.q && query.q.trim()) return true;
    return (
      query.caliber.length > 0 ||
      query.ammoType.length > 0 ||
      query.brand.length > 0 ||
      query.productLine.length > 0 ||
      query.bulletType.length > 0 ||
      query.bulletWeightGrains.length > 0 ||
      query.caseMaterial.length > 0 ||
      query.retailerId.length > 0 ||
      query.useCases.length > 0 ||
      query.minPricePerRound != null ||
      query.maxPricePerRound != null ||
      query.minRoundCount != null ||
      (query.stockStatus?.length ?? 0) > 0 ||
      query.inStockOnly === true ||
      query.excludeRemanufactured === true ||
      Boolean(query.lotNumber)
    );
  }, [query]);

  const latestUpdate = mostRecentUpdate(searchQuery.data);
  const isLoadingInitial = searchQuery.isLoading && !searchQuery.data;
  const is503 = searchQuery.error instanceof ApiError && searchQuery.error.httpStatus === 503;

  const seoTitle = buildSeoTitle(query);
  const seoDescription = buildSeoDescription(query, searchQuery.data);
  // A bare single-caliber facet consolidates onto its /ammo/:slug landing page
  // (#250) so the two don't compete as duplicate content; everything else is
  // self-canonical to the search URL.
  const landingPath = caliberLandingPathForSearch(query);
  const canonical = landingPath
    ? buildCanonicalUrl(landingPath)
    : buildCanonicalUrl('/search', searchParams);
  const seoJsonLd = buildSearchJsonLd(searchQuery.data, canonical, query.caliber);

  // Keep thin/empty result pages out of the index to protect crawl budget on
  // the (effectively infinite) filter-combination space — they carry no unique
  // value and would otherwise be soft-thin duplicates.
  const noindexResults = searchQuery.data != null && searchQuery.data.totalResults === 0;

  // Lowest delivered $/round across the current result page (real data).
  const lowCpr = useMemo(() => {
    const values = (searchQuery.data?.results ?? [])
      .map((hit) => hit.pricePerRound)
      .filter((v): v is number => typeof v === 'number' && Number.isFinite(v) && v > 0);
    return values.length > 0 ? Math.min(...values) : null;
  }, [searchQuery.data]);

  const crumbCaliber = query.caliber[0];

  return (
    <Box>
      <Seo
        title={seoTitle}
        description={seoDescription}
        canonical={canonical}
        noindex={noindexResults}
        jsonLd={seoJsonLd}
      />

      {/* Tactical sub-nav band: breadcrumb + result summary */}
      <Box sx={{ mb: 2.5 }}>
        <Box
          component="nav"
          aria-label="Breadcrumb"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontFamily: t.monoFont,
            fontSize: 11,
            color: t.dim,
            mb: 1,
          }}
        >
          <Box
            component={RouterLink}
            to="/"
            sx={{ color: t.dim, textDecoration: 'none', '&:hover': { color: t.text } }}
          >
            HOME
          </Box>
          <Box component="span" sx={{ color: t.dimmer }}>
            /
          </Box>
          <Box component="span">SEARCH</Box>
          {crumbCaliber && (
            <>
              <Box component="span" sx={{ color: t.dimmer }}>
                /
              </Box>
              <Box component="span" sx={{ color: t.accent, textTransform: 'uppercase' }}>
                {crumbCaliber}
              </Box>
            </>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h1" sx={{ fontSize: '1.6rem' }}>
            Search results
          </Typography>
          {searchQuery.data && (
            <Typography
              component="span"
              sx={{ fontFamily: t.monoFont, fontSize: 13, color: t.dim }}
            >
              <Box component="span" sx={{ color: t.text }}>
                {searchQuery.data.totalResults.toLocaleString()}
              </Box>{' '}
              results
              {lowCpr != null && (
                <>
                  {' · '}
                  <Box component="span" sx={{ color: t.accent }}>
                    ${lowCpr.toFixed(3)}
                  </Box>{' '}
                  low $/rd
                </>
              )}
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
        <FilterSidebar
          facets={searchQuery.data?.facets ?? {}}
          searchParams={searchParams}
          onChange={setSearchParams}
        />

        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <ActiveFilterChips searchParams={searchParams} setSearchParams={setSearchParams} />

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
          >
            <Box aria-live="polite">
              {searchQuery.data ? (
                <Typography variant="body2">
                  Showing {searchQuery.data.results.length} of {searchQuery.data.totalResults}{' '}
                  listings
                  {latestUpdate ? ` — last updated ${latestUpdate}` : ''}
                </Typography>
              ) : null}
            </Box>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
              <SaveSearchButton
                searchParams={searchParams}
                defaultName={seoTitle}
                hasCriteria={hasCriteria}
              />
              <ToggleButtonGroup
                exclusive
                size="small"
                value={viewMode}
                onChange={onViewModeChange}
                aria-label="Result layout"
              >
                <ToggleButton value="cards" aria-label="Card view">
                  <GridViewIcon fontSize="small" />
                  <Box component="span" sx={{ ml: 0.5, display: { xs: 'none', sm: 'inline' } }}>
                    Cards
                  </Box>
                </ToggleButton>
                <ToggleButton value="table" aria-label="Table view">
                  <TableRowsIcon fontSize="small" />
                  <Box component="span" sx={{ ml: 0.5, display: { xs: 'none', sm: 'inline' } }}>
                    Table
                  </Box>
                </ToggleButton>
              </ToggleButtonGroup>
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel id="search-sort-label">Sort</InputLabel>
                <Select
                  labelId="search-sort-label"
                  id="search-sort"
                  value={query.sort}
                  label="Sort"
                  onChange={onSortChange}
                >
                  {SORT_LABELS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Stack>

          {/* Sponsored slot — labeled visually + programmatically per
              spec §28.4. Suppression (stale, OOS, consent opt-out) and
              the MAX_SPONSORED display cap are handled inside the
              component (§23.3). */}
          <SponsoredListingsSection hits={searchQuery.data?.results ?? []} />

          {/* Promoted-retailer slot (#149, §23) — pinned below the paid
              sponsored slots, labeled visually + programmatically. Promotion
              is editorial, not behavioral, so it is NOT consent-gated; the
              §23.3 stale/OOS suppression + the MAX_PROMOTED cap are handled
              inside the component. */}
          <PromotedListingsSection hits={searchQuery.data?.results ?? []} />

          {is503 ? (
            <Alert severity="warning" role="alert">
              The search index isn&apos;t ready yet. Please retry in a moment.
              <Box sx={{ mt: 1 }}>
                <Button variant="outlined" onClick={() => searchQuery.refetch()}>
                  Retry
                </Button>
              </Box>
            </Alert>
          ) : null}

          {searchQuery.isError && !is503 ? (
            <Alert severity="error" role="alert">
              Search failed: {(searchQuery.error as Error).message}
            </Alert>
          ) : null}

          {isLoadingInitial ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  lg: 'repeat(3, 1fr)',
                },
                gap: 2,
              }}
              aria-busy="true"
              aria-label="Loading search results"
            >
              {Array.from({ length: 6 }).map((_, i) => (
                <Paper key={i} variant="outlined" sx={{ p: 2 }}>
                  <Skeleton variant="text" width="60%" />
                  <Skeleton variant="text" width="40%" />
                  <Skeleton variant="rectangular" height={60} sx={{ mt: 1 }} />
                </Paper>
              ))}
            </Box>
          ) : null}

          {searchQuery.data && searchQuery.data.results.length === 0 && !searchQuery.isFetching ? (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h3" sx={{ fontSize: '1.125rem', mb: 1 }}>
                No listings match your filters.
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Try removing a filter or broadening your search.
              </Typography>
              <Button variant="contained" onClick={onClearFilters}>
                Clear filters
              </Button>
            </Paper>
          ) : null}

          {searchQuery.data && searchQuery.data.results.length > 0 ? (
            <>
              {viewMode === 'table' ? (
                <ListingTable
                  hits={searchQuery.data.results.filter(
                    (hit) => !hit.isSponsored && !hit.isPromoted
                  )}
                  sort={query.sort}
                  sortDir={query.sortDir}
                  onSortChange={onSortChangeFromTable}
                />
              ) : (
                <Box
                  component="ul"
                  sx={{
                    listStyle: 'none',
                    p: 0,
                    m: 0,
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(2, 1fr)',
                      lg: 'repeat(3, 1fr)',
                    },
                    gap: 2,
                  }}
                  aria-label="Search results"
                >
                  {searchQuery.data.results
                    .filter((hit) => !hit.isSponsored && !hit.isPromoted)
                    .map((hit) => (
                      <Box component="li" key={hit.listingId} sx={{ display: 'flex' }}>
                        <Box sx={{ width: '100%' }}>
                          <ListingCard hit={hit} variant="full" />
                        </Box>
                      </Box>
                    ))}
                </Box>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Pagination
                  count={totalPages}
                  page={query.page}
                  onChange={onPageChange}
                  aria-label="Search results pagination"
                />
              </Box>
            </>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}

export default SearchResultsPage;
